// src/components/PageLoader.jsx
import React from "react";
import styled, { keyframes } from "styled-components";

/*
  Shown while a lazily-loaded route chunk downloads, and as the
  initial boot screen. Deliberately minimal — it should feel like
  the first frame of the film, not a spinner.
*/
const PageLoader = () => (
  <Wrap>
    <div className="mark">
      <span className="a">A</span>
      <span className="ring" />
    </div>
    <span className="label">ASites</span>
  </Wrap>
);

const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`;

const Wrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #08040c;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.4em;

  .mark {
    position: relative;
    width: 74px;
    height: 74px;
    display: grid;
    place-items: center;
  }
  .a {
    font-family: "Readex Pro", sans-serif;
    font-weight: 200;
    font-size: 2rem;
    color: #9281dd;
    animation: ${pulse} 1.6s ease-in-out infinite;
  }
  .ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid #1d1530;
    border-top-color: #9281dd;
    animation: ${spin} 0.9s linear infinite;
  }
  .label {
    font-family: "Montserrat", sans-serif;
    letter-spacing: 0.5em;
    font-size: 0.7rem;
    color: #463d6e;
    text-transform: uppercase;
    text-indent: 0.5em;
  }
`;

export default PageLoader;

/* A thin scroll-progress bar for the long scroll-driven pages.
   Uses a passive listener + rAF so it never blocks scrolling. */
export const ScrollProgress = () => {
  const barRef = React.useRef(null);

  React.useEffect(() => {
    let raf = null;
    const update = () => {
      raf = null;
      const h =
        document.documentElement.scrollHeight - window.innerHeight;
      const p = h > 0 ? window.scrollY / h : 0;
      if (barRef.current)
        barRef.current.style.transform = `scaleX(${Math.min(p, 1)})`;
    };
    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);

  return <Bar ref={barRef} />;
};

const Bar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 2px;
  width: 100%;
  z-index: 10000;
  transform: scaleX(0);
  transform-origin: left;
  background: linear-gradient(90deg, #9281dd, #b9c5fb);
  pointer-events: none;
  will-change: transform;
`;
